// src/components/ecommerce/StockAdjustmentModal.jsx
import React, { useState } from 'react';
import { 
  X,
  Plus,
  Minus,
  Package,
  Save,
  AlertTriangle,
  User,
  FileText,
  Calendar,
  Building
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';

export const StockAdjustmentModal = ({ product, suppliers, isOpen, onClose, onUpdate }) => {
  const [adjustmentData, setAdjustmentData] = useState({
    type: 'adjustment',
    quantity: '',
    reason: '',
    notes: '',
    supplierId: '',
    reference: '',
    cost: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const adjustmentTypes = [
    { value: 'in', label: 'Stock In', icon: Plus, color: 'green' },
    { value: 'out', label: 'Stock Out', icon: Minus, color: 'red' },
    { value: 'adjustment', label: 'Adjustment', icon: Package, color: 'blue' },
    { value: 'transfer', label: 'Transfer', icon: Package, color: 'purple' },
    { value: 'damaged', label: 'Damaged', icon: AlertTriangle, color: 'orange' },
    { value: 'returned', label: 'Returned', icon: Package, color: 'indigo' }
  ];

  const reasonOptions = {
    in: [
      'Purchase from supplier',
      'Return from customer',
      'Transfer from another location',
      'Production increase',
      'Found inventory',
      'Other'
    ],
    out: [
      'Sale to customer',
      'Transfer to another location',
      'Damaged goods',
      'Expired products',
      'Theft or loss',
      'Promotional giveaway',
      'Other'
    ],
    adjustment: [
      'Inventory count correction',
      'System error correction',
      'Damaged inventory write-off',
      'Shrinkage adjustment',
      'Other'
    ],
    transfer: [
      'Location transfer',
      'Warehouse relocation',
      'Store restock',
      'Other'
    ],
    damaged: [
      'Product defect',
      'Shipping damage',
      'Storage damage',
      'Customer return - damaged',
      'Other'
    ],
    returned: [
      'Customer return - unwanted',
      'Customer return - wrong item',
      'Supplier return',
      'Quality issue return',
      'Other'
    ]
  };

  const handleInputChange = (field, value) => {
    setAdjustmentData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!adjustmentData.quantity || adjustmentData.quantity === '0') {
      newErrors.quantity = 'Quantity is required and must be greater than 0';
    }

    if (!adjustmentData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if (adjustmentData.type === 'out' && parseInt(adjustmentData.quantity) > (product?.quantity || 0)) {
      newErrors.quantity = `Cannot remove more than current stock (${product?.quantity || 0})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const quantity = parseInt(adjustmentData.quantity);
      const currentStock = product?.quantity || 0;
      
      let newStock;
      switch (adjustmentData.type) {
        case 'in':
        case 'returned':
          newStock = currentStock + quantity;
          break;
        case 'out':
        case 'damaged':
          newStock = Math.max(0, currentStock - quantity);
          break;
        case 'adjustment':
          // For adjustments, the quantity represents the new total
          newStock = quantity;
          break;
        case 'transfer':
          newStock = Math.max(0, currentStock - quantity);
          break;
        default:
          newStock = currentStock;
      }

      const movementData = {
        productId: product.id,
        productName: product.name,
        sku: product.sku,
        type: adjustmentData.type,
        quantity: adjustmentData.type === 'adjustment' ? Math.abs(newStock - currentStock) : quantity,
        previousStock: currentStock,
        newStock: newStock,
        reason: adjustmentData.reason,
        notes: adjustmentData.notes,
        supplierId: adjustmentData.supplierId || null,
        reference: adjustmentData.reference,
        cost: adjustmentData.cost ? parseFloat(adjustmentData.cost) : null,
        createdAt: new Date(),
        createdBy: 'Admin' // This should come from user context
      };

      // Save movement and update product stock
      await ecommerceService.addInventoryMovement(movementData);
      await ecommerceService.updateProductStock(product.id, newStock);

      onUpdate?.();
      onClose();
      
      // Reset form
      setAdjustmentData({
        type: 'adjustment',
        quantity: '',
        reason: '',
        notes: '',
        supplierId: '',
        reference: '',
        cost: ''
      });
      
    } catch (error) {
      console.error('Error saving stock adjustment:', error);
      setErrors({ submit: 'Failed to save adjustment. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const getNewStockLevel = () => {
    if (!adjustmentData.quantity) return product?.quantity || 0;
    
    const quantity = parseInt(adjustmentData.quantity);
    const currentStock = product?.quantity || 0;
    
    switch (adjustmentData.type) {
      case 'in':
      case 'returned':
        return currentStock + quantity;
      case 'out':
      case 'damaged':
        return Math.max(0, currentStock - quantity);
      case 'adjustment':
        return quantity;
      case 'transfer':
        return Math.max(0, currentStock - quantity);
      default:
        return currentStock;
    }
  };

  const selectedType = adjustmentTypes.find(type => type.value === adjustmentData.type);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Stock Adjustment</h2>
              <p className="text-gray-600">{product?.name}</p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Product Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
              {product?.images?.[0] || product?.imageUrl ? (
                <img
                  src={product.images?.[0] || product.imageUrl}
                  alt={product.name}
                  className="h-12 w-12 object-cover rounded-lg"
                />
              ) : (
                <Package className="h-6 w-6 text-gray-400" />
              )}
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{product?.name}</h3>
              <p className="text-gray-600">SKU: {product?.sku}</p>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-600">Current Stock</p>
              <p className="text-2xl font-bold text-gray-900">{product?.quantity || 0}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Adjustment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Adjustment Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {adjustmentTypes.map(type => {
                const Icon = type.icon;
                const isSelected = adjustmentData.type === type.value;
                
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('type', type.value)}
                    className={`p-3 border-2 rounded-lg transition-colors ${
                      isSelected
                        ? `border-${type.color}-500 bg-${type.color}-50`
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mx-auto mb-2 ${
                      isSelected ? `text-${type.color}-600` : 'text-gray-600'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isSelected ? `text-${type.color}-700` : 'text-gray-700'
                    }`}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {adjustmentData.type === 'adjustment' ? 'New Stock Level' : 'Quantity'} *
            </label>
            <input
              type="number"
              min="0"
              value={adjustmentData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.quantity ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={adjustmentData.type === 'adjustment' ? 'Enter new stock level' : 'Enter quantity'}
            />
            {errors.quantity && (
              <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
            )}
            
            {adjustmentData.quantity && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>New stock level will be:</strong> {getNewStockLevel()} units
                  {adjustmentData.type !== 'adjustment' && (
                    <span> ({adjustmentData.type === 'in' || adjustmentData.type === 'returned' ? '+' : '-'}{adjustmentData.quantity})</span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason *
            </label>
            <select
              value={adjustmentData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.reason ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select a reason</option>
              {(reasonOptions[adjustmentData.type] || []).map(reason => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
            )}
          </div>

          {/* Supplier (for stock in) */}
          {(adjustmentData.type === 'in' || adjustmentData.type === 'returned') && suppliers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier
              </label>
              <select
                value={adjustmentData.supplierId}
                onChange={(e) => handleInputChange('supplierId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select supplier (optional)</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Reference and Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference #
              </label>
              <input
                type="text"
                value={adjustmentData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="PO #, Invoice #, etc."
              />
            </div>

            {adjustmentData.type === 'in' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Cost
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={adjustmentData.cost}
                  onChange={(e) => handleInputChange('cost', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              value={adjustmentData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional information..."
            />
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              disabled={saving || !adjustmentData.quantity || !adjustmentData.reason}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Saving...' : 'Save Adjustment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};